import React,{useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiInfo,FiChevronDown,FiCalendar,FiAward,FiCheckCircle}=FiIcons;

// Expanded Racing Sports data with multiple categories
const racingData={
  'formula1': {
    name: 'Formula 1',
    races: [
      {
        id: 1,
        race: 'Monaco Grand Prix',
        date: '2024-05-26',
        circuit: 'Circuit de Monaco',
        drivers: ['Max Verstappen','Lewis Hamilton','Charles Leclerc','Lando Norris','George Russell','Carlos Sainz','Sergio Perez','Fernando Alonso','Oscar Piastri','Esteban Ocon','Pierre Gasly','Yuki Tsunoda']
      },
      {
        id: 2,
        race: 'British Grand Prix',
        date: '2024-07-07',
        circuit: 'Silverstone Circuit',
        drivers: ['Max Verstappen','Lewis Hamilton','Charles Leclerc','Lando Norris','George Russell','Carlos Sainz','Oscar Piastri','Fernando Alonso','Sergio Perez','Esteban Ocon','Pierre Gasly','Yuki Tsunoda']
      }
    ]
  },
  'motogp': {
    name: 'MotoGP',
    races: [
      {
        id: 11,
        race: 'Italian Grand Prix',
        date: '2024-06-02',
        circuit: 'Mugello Circuit',
        drivers: ['Francesco Bagnaia','Jorge Martin','Marco Bezzecchi','Brad Binder','Jack Miller','Maverick Viñales','Aleix Espargaro','Johann Zarco','Pedro Acosta','Enea Bastianini','Marc Marquez','Fabio Quartararo']
      },
    ]
  },
  'indycar': {
    name: 'IndyCar',
    races: [
      {
        id: 21,
        race: 'Indianapolis 500',
        date: '2024-05-26',
        circuit: 'Indianapolis Motor Speedway',
        drivers: ['Alex Palou','Scott Dixon','Josef Newgarden','Pato O\'Ward','Marcus Ericsson','Colton Herta','Will Power','Scott McLaughlin','Alexander Rossi','Kyle Kirkwood','Felix Rosenqvist','Romain Grosjean']
      },
    ]
  },
  'rally': {
    name: 'WRC Rally',
    races: [
      {
        id: 31,
        race: 'Rally Monte Carlo',
        date: '2024-01-25',
        circuit: 'Monte Carlo,Monaco',
        drivers: ['Sébastien Ogier','Kalle Rovanperä','Thierry Neuville','Ott Tänak','Elfyn Evans','Esapekka Lappi','Takamoto Katsuta','Adrien Fourmaux','Andreas Mikkelsen','Grégoire Munster']
      },
    ]
  },
  'nascar': {
    name: 'NASCAR Cup Series',
    races: [
      {
        id: 41,
        race: 'Daytona 500',
        date: '2024-02-18',
        circuit: 'Daytona International Speedway',
        drivers: ['Kyle Larson','Chase Elliott','Ryan Blaney','William Byron','Denny Hamlin','Christopher Bell','Tyler Reddick','Ross Chastain','Martin Truex Jr.','Joey Logano','Kyle Busch','Brad Keselowski']
      },
    ]
  }
};

const RacingSportsCard=({onStake})=> {
  const [selectedCategory,setSelectedCategory]=useState('formula1');
  const [selectedRace,setSelectedRace]=useState('all');
  const [predictions,setPredictions]=useState({});
  const [isCategoryDropdownOpen,setIsCategoryDropdownOpen]=useState(false);
  const [isRaceDropdownOpen,setIsRaceDropdownOpen]=useState(false);

  const categories=[
    {key: 'formula1',name: 'Formula 1',emoji: '🏎️'},
    {key: 'motogp',name: 'MotoGP',emoji: '🏍️'},
    {key: 'indycar',name: 'IndyCar',emoji: '🏁'},
    {key: 'rally',name: 'WRC Rally',emoji: '🚗'},
    {key: 'nascar',name: 'NASCAR',emoji: '🏆'}
  ];

  const getAvailableRaces=()=> {
    const races=[{key: 'all',name: 'All Races'}];
    const categoryData=racingData[selectedCategory];
    if (categoryData) {
      categoryData.races.forEach(race=> {
        races.push({key: race.id.toString(),name: race.race,race: race});
      });
    }
    return races;
  };

  const getRacesToShow=()=> {
    const categoryData=racingData[selectedCategory];
    if (!categoryData) return [];
    if (selectedRace==='all') {
      return categoryData.races;
    } else {
      const race=categoryData.races.find(r=> r.id.toString()===selectedRace);
      return race ? [race] : [];
    }
  };

  const handlePrediction=(raceId,position,driver)=> {
    setPredictions(prev=> {
      const newRacePredictions={...(prev[raceId] || {})};

      // Clear the new driver from any other position it might occupy
      for (const pos in newRacePredictions) {
        if (newRacePredictions[pos]===driver) {
          delete newRacePredictions[pos];
        }
      }
      
      newRacePredictions[`pos${position}`]=driver;

      return {...prev,[raceId]: newRacePredictions};
    });
  };

  const getAvailableDrivers=(race,raceId,currentPosition)=> {
    const racePredictions=predictions[raceId] || {};
    const selectedDrivers=Object.values(racePredictions);
    const currentDriverForPosition=racePredictions[`pos${currentPosition}`];

    return race.drivers.filter(driver=> 
      !selectedDrivers.includes(driver) || driver===currentDriverForPosition
    );
  };
  
  const isPredictionComplete=(raceId)=> {
    const racePredictions=predictions[raceId] || {};
    const selectedCount=Object.keys(racePredictions).length;
    return selectedCount===6;
  };

  const handleStake=(race)=> {
    const prediction=predictions[race.id];
    if (isPredictionComplete(race.id)) {
      onStake({
        type: 'racing-sports',
        category: racingData[selectedCategory].name,
        race: race,
        prediction: prediction,
        sport: 'Racing Sports'
      });
    }
  };

  const racesToShow=getRacesToShow();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Compact Instructions */}
      <motion.div
        className="bg-bull-charcoal border border-bull-red/20 rounded-bull p-3 sm:p-4 shadow-bull"
        initial={{opacity: 0,y: -10}}
        animate={{opacity: 1,y: 0}}
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <SafeIcon icon={FiInfo} className="w-4 h-4 sm:w-5 sm:h-5 text-bull-yellow flex-shrink-0" />
          <div>
            <h3 className="font-heading text-xs sm:text-sm font-bold text-bull-white mb-1">
              Racing Sports Predictions
            </h3>
            <p className="text-xs text-bull-light-gray">
              Select a racing category and predict the correct finishing order for the top 6 positions to stake.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Category and Race Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {/* Category Selector */}
        <div className="relative">
          <motion.button
            className="w-full bg-bull-charcoal border border-bull-light-gray rounded-bull px-3 sm:px-4 py-3 flex items-center justify-between text-white hover:border-bull-red transition-all duration-300"
            onClick={()=> setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            whileHover={{scale: 1.01}} whileTap={{scale: 0.99}}
          >
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <span className="text-lg sm:text-xl">
                {categories.find(c=> c.key===selectedCategory)?.emoji || '🏎️'}
              </span>
              <span className="font-medium text-xs sm:text-sm truncate">
                {categories.find(c=> c.key===selectedCategory)?.name || 'Formula 1'}
              </span>
            </div>
            <motion.div
              animate={{rotate: isCategoryDropdownOpen ? 180 : 0}}
              transition={{duration: 0.2}}
              className="flex-shrink-0"
            >
              <SafeIcon icon={FiChevronDown} className="w-3 h-3 sm:w-4 sm:h-4 text-bull-light-gray" />
            </motion.div>
          </motion.button>
          {isCategoryDropdownOpen && (
            <motion.div
              initial={{opacity: 0,y: -10}} animate={{opacity: 1,y: 0}} exit={{opacity: 0,y: -10}}
              className="absolute top-full left-0 right-0 mt-2 bg-bull-charcoal border border-bull-light-gray rounded-bull shadow-bull-lg z-50 overflow-hidden"
            >
              {categories.map((category)=> (
                <motion.button
                  key={category.key}
                  className="w-full px-3 sm:px-4 py-3 text-left hover:bg-bull-gray transition-colors border-b border-bull-gray last:border-b-0 flex items-center gap-3"
                  onClick={()=> {
                    setSelectedCategory(category.key);
                    setSelectedRace('all');
                    setIsCategoryDropdownOpen(false);
                    setPredictions({});
                  }}
                  whileHover={{backgroundColor: '#2A3132'}}
                >
                  <span className="text-lg">{category.emoji}</span>
                  <div className="font-medium text-white text-xs sm:text-sm">{category.name}</div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>
        {/* Race Selector */}
        <div className="relative">
          <motion.button
            className="w-full bg-bull-charcoal border border-bull-light-gray rounded-bull px-3 sm:px-4 py-3 flex items-center justify-between text-white hover:border-bull-red transition-all duration-300"
            onClick={()=> setIsRaceDropdownOpen(!isRaceDropdownOpen)}
            whileHover={{scale: 1.01}} whileTap={{scale: 0.99}}
          >
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <SafeIcon icon={FiCalendar} className="w-3 h-3 sm:w-4 sm:h-4 text-bull-orange flex-shrink-0" />
              <span className="font-medium text-xs sm:text-sm truncate">
                {getAvailableRaces().find(r=> r.key===selectedRace)?.name || 'All Races'}
              </span>
            </div>
            <motion.div
              animate={{rotate: isRaceDropdownOpen ? 180 : 0}}
              transition={{duration: 0.2}}
              className="flex-shrink-0"
            >
              <SafeIcon icon={FiChevronDown} className="w-3 h-3 sm:w-4 sm:h-4 text-bull-light-gray" />
            </motion.div>
          </motion.button>
          {isRaceDropdownOpen && (
            <motion.div
              initial={{opacity: 0,y: -10}} animate={{opacity: 1,y: 0}} exit={{opacity: 0,y: -10}}
              className="absolute top-full left-0 right-0 mt-2 bg-bull-charcoal border border-bull-light-gray rounded-bull shadow-bull-lg z-50 max-h-48 sm:max-h-60 overflow-y-auto"
            >
              {getAvailableRaces().map((race)=> (
                <motion.button
                  key={race.key}
                  className="w-full px-3 sm:px-4 py-3 text-left hover:bg-bull-gray transition-colors border-b border-bull-gray last:border-b-0"
                  onClick={()=> {
                    setSelectedRace(race.key);
                    setIsRaceDropdownOpen(false);
                    setPredictions({});
                  }}
                  whileHover={{backgroundColor: '#2A3132'}}
                >
                  <div className="font-medium text-white text-xs sm:text-sm">{race.name}</div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Race Cards */}
      <div className="space-y-4 sm:space-y-6">
        {racesToShow.map((race,index)=> {
          const finishedPositions=Object.keys(predictions[race.id] || {}).length;
          const complete=isPredictionComplete(race.id);
          return (
            <motion.div
              key={race.id}
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{delay: index * 0.1}}
              className="bg-bull-charcoal rounded-bull p-3 sm:p-4 shadow-bull border border-bull-gray hover:border-bull-red/30 transition-all duration-300"
              whileHover={{scale: 1.01}}
            >
              {/* Race Header */}
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center gap-2 mb-2">
                   <span className="text-xl sm:text-2xl">
                    {categories.find(c=> c.key===selectedCategory)?.emoji}
                  </span>
                  <h3 className="font-heading font-bold text-bull-red text-base sm:text-xl uppercase tracking-wide">
                    {race.race}
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm text-bull-light-gray">
                  <span>{race.date}</span>
                  <span>{race.circuit}</span>
                </div>
                 <div className="text-bull-yellow text-xs sm:text-sm mt-1">
                  {racingData[selectedCategory].name}
                </div>
              </div>
              
              {/* Top 6 Prediction Grid */}
              <div className="mb-4 sm:mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-bull-yellow font-bold text-sm">Predict Top 6 Finishers</h4>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-bull-light-gray">{finishedPositions} / 6</span>
                    {complete && <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-bull-yellow" />}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[1,2,3,4,5,6].map(pos=> {
                     const selectedDriver=predictions[race.id]?.[`pos${pos}`];
                     return (
                      <motion.div 
                        key={pos}
                        initial={{opacity: 0,scale: 0.95}}
                        animate={{opacity: 1,scale: 1}}
                        transition={{delay: pos * 0.05}}
                        className="bg-bull-gray p-3 rounded-bull border border-bull-charcoal"
                      >
                        <label className="flex items-center gap-2 text-bull-light-gray text-xs font-medium mb-2">
                          <SafeIcon icon={FiAward} className={`w-4 h-4 ${pos <=3 ? 'text-bull-yellow' : 'text-bull-light-gray'}`} />
                          Position {pos}
                        </label>
                        <select
                          value={selectedDriver || ''}
                          onChange={(e)=> handlePrediction(race.id,pos,e.target.value)}
                          className={`w-full text-xs sm:text-sm bg-bull-charcoal border rounded px-2 py-2 text-white focus:outline-none transition-colors ${selectedDriver ? 'border-bull-red/50' : 'border-bull-light-gray focus:border-bull-red'}`}
                        >
                          <option value="">Select driver...</option>
                          {getAvailableDrivers(race,race.id,pos).map(driver=> (
                            <option key={driver} value={driver}>{driver}</option>
                          ))}
                        </select>
                      </motion.div>
                     )
                  })}
                </div>
              </div>

              {/* Stake Button */}
              <motion.button
                className={`w-full py-2.5 sm:py-3 rounded-bull font-bold text-xs sm:text-sm transition-all duration-300 ${complete ? 'bg-gradient-to-r from-bull-red to-bull-red-light hover:from-bull-red-light hover:to-bull-red text-white' : 'bg-bull-gray text-bull-light-gray cursor-not-allowed'}`}
                onClick={()=> handleStake(race)}
                disabled={!complete}
                whileHover={complete ? {scale: 1.02} : {}}
                whileTap={complete ? {scale: 0.98} : {}}
              >
                {complete ? 'STAKE PREDICTION' : 'COMPLETE TOP 6 PREDICTION'}
              </motion.button>
            </motion.div>
          )
        })}
      </div>
       {racesToShow.length===0 && (
        <div className="text-center py-8 sm:py-12">
          <div className="text-3xl sm:text-4xl mb-4">🏁</div>
          <h3 className="text-lg sm:text-xl font-bold text-bull-white mb-2">No Races Available</h3>
          <p className="text-bull-light-gray text-sm sm:text-base">
            No races available for the selected category
          </p>
        </div>
      )}
    </div>
  );
};

export default RacingSportsCard;